// src/AdminPages/home/AddSlider/AddSlider.jsx

import React, { useState, useMemo, useRef, useEffect } from 'react';
import Swal from 'sweetalert2';
import { 
    FaEdit, FaTrashAlt, FaTimes, FaUpload, 
    FaSearch, FaChevronDown, FaChevronUp, FaPlus, FaExchangeAlt 
} from 'react-icons/fa';
import './AddSlider.css';
// --- CHANGE 1: Import our new global axios instance ---
import axiosInstance from '../../../api/axiosInstance';

// The API_BASE_URL is still needed for displaying images, but not for API calls.
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

// --- MODAL COMPONENT (No changes needed inside this component) ---
const SliderFormModal = ({ isOpen, onClose, slide, mode, onSave }) => {
    const [formData, setFormData] = useState({ type: 'image', src: '', heading: '', subText: '', isActive: true });
    const [file, setFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState('');
    const fileInputRef = useRef(null);

    useEffect(() => {
        if (isOpen) {
            if (slide) {
                setFormData(slide);
                setPreviewUrl(slide.src ? `${API_BASE_URL}${slide.src}` : '');
                setFile(null);
            } else {
                setFormData({ type: 'image', src: '', heading: '', subText: '', isActive: true });
                setPreviewUrl('');
                setFile(null);
            }
        }
    }, [slide, isOpen]);

    const handleTabChange = (type) => {
        if (mode === 'view') return;
        setFormData(prev => ({ ...prev, type }));
        setPreviewUrl('');
        setFile(null);
    };

    const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            setPreviewUrl(URL.createObjectURL(selectedFile));
        }
    };

    const handleRemoveMedia = (e) => {
        e.stopPropagation();
        setFile(null);
        setPreviewUrl('');
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const handleSubmit = () => {
        if (mode === 'add' && !file) return Swal.fire('Error', 'Please upload media', 'error');
        
        const submission = new FormData();
        submission.append('type', formData.type);
        submission.append('heading', formData.heading || '');
        submission.append('subText', formData.subText || '');
        submission.append('isActive', formData.isActive);
        if (file) submission.append('media', file);

        onSave(submission);
    };

    if (!isOpen) return null;
    const isView = mode === 'view';

    return (
        <div className="add-slider-modal-overlay" onClick={onClose}>
            <div className="add-slider-modal-content" onClick={e => e.stopPropagation()}>
                <div className="add-slider-modal-header">
                    <h2>{isView ? 'View Slider Details' : slide ? 'Edit Slider' : 'Add New Slider'}</h2>
                    <button className="add-slider-modal-close-btn" onClick={onClose}><FaTimes /></button>
                </div>
                <div className="add-slider-modal-body">
                    <div className="add-slider-tabs">
                        <button className={`add-slider-tab ${formData.type === 'image' ? 'active' : ''}`} onClick={() => handleTabChange('image')} disabled={isView}>Image</button>
                        <button className={`add-slider-tab ${formData.type === 'video' ? 'active' : ''}`} onClick={() => handleTabChange('video')} disabled={isView}>Video</button>
                    </div>
                    <div className="add-slider-preview-container" onClick={() => !isView && fileInputRef.current.click()}>
                        {previewUrl ? (
                            <div className="add-slider-media-display">
                                {formData.type === 'image' ? <img src={previewUrl} alt="Preview" onError={(e) => e.target.src='https://via.placeholder.com/300?text=Error'} /> : <video src={previewUrl} controls={isView} muted={!isView} />}
                                {!isView && (<div className="add-slider-preview-overlay"><button className="add-slider-overlay-btn replace"><FaExchangeAlt /> Replace</button><button className="add-slider-overlay-btn remove" onClick={handleRemoveMedia}><FaTrashAlt /> Remove</button></div>)}
                            </div>
                        ) : (
                            <div className="add-slider-upload-prompt"><FaUpload className="add-slider-upload-icon"/><span>Click to Upload {formData.type === 'image' ? 'Image' : 'Video'}</span></div>
                        )}
                        <input type="file" ref={fileInputRef} hidden onChange={handleFileChange} accept={formData.type === 'image' ? 'image/*' : 'video/*'} />
                    </div>
                    <div className="add-slider-form-group"><label>Heading Text <small>(Optional)</small></label><input type="text" name="heading" value={formData.heading} onChange={handleChange} className="add-slider-input" placeholder="Enter main heading..." disabled={isView}/></div>
                    <div className="add-slider-form-group"><label>Sub Text <small>(Optional)</small></label><input type="text" name="subText" value={formData.subText} onChange={handleChange} className="add-slider-input" placeholder="Enter subtitle or description..." disabled={isView}/></div>
                </div>
                <div className="add-slider-modal-footer">
                    <button className="add-slider-btn-cancel" onClick={onClose}>{isView ? 'Close' : 'Cancel'}</button>
                    {!isView && <button className="add-slider-btn-save" onClick={handleSubmit}>Save Changes</button>}
                </div>
            </div>
        </div>
    );
};

// --- MAIN COMPONENT ---
const AddSlider = () => {
    const [slides, setSlides] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('add'); 
    const [selectedSlide, setSelectedSlide] = useState(null);
    const [expandedRow, setExpandedRow] = useState(null);

    const toggleRow = (id) => setExpandedRow(prev => (prev === id ? null : id));
    const formatDate = (d) => d ? new Date(d).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '-';

    const fetchSliders = async () => {
        try {
            // --- CHANGE 2: Use axiosInstance to fetch data ---
            const res = await axiosInstance.get('/api/sliders');
            setSlides(res.data);
        } catch (error) {
            // Interceptor handles 401. This catches other errors.
            if (error.response?.status !== 401) {
                console.error(error);
                Swal.fire('Error', 'Could not fetch sliders from the server.', 'error');
            }
        }
    };

    useEffect(() => { fetchSliders(); }, []);

    // Your Search & Pagination logic remains the same
    const filteredSlides = useMemo(() => {
        const term = searchTerm.trim().toLowerCase();
        if (!term) return slides;
        return slides.filter(s =>
            (s.heading && s.heading.toLowerCase().includes(term)) ||
            (s.subText && s.subText.toLowerCase().includes(term))
        );
    }, [slides, searchTerm]);

    const totalResults = filteredSlides.length;
    const totalPages = Math.ceil(totalResults / itemsPerPage);
    const startResult = totalResults === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
    const endResult = Math.min(currentPage * itemsPerPage, totalResults);
    const paginatedSlides = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        return filteredSlides.slice(startIndex, startIndex + itemsPerPage);
    }, [filteredSlides, currentPage, itemsPerPage]);

    const handleSave = async (formData) => {
        const url = modalMode === 'edit' ? `/api/sliders/${selectedSlide._id}` : `/api/sliders`;
        const method = modalMode === 'edit' ? 'put' : 'post';

        try {
            // --- CHANGE 3: Use axiosInstance for saving data ---
            await axiosInstance({ method, url, data: formData });
            
            await fetchSliders();
            Swal.fire(modalMode === 'edit' ? 'Updated!' : 'Added!', '', 'success');
            setIsModalOpen(false);
        } catch (error) {
            if (error.response?.status !== 401) {
                Swal.fire('Error', error.response?.data?.message || 'Operation failed', 'error');
            }
        }
    };

    const handleDelete = (id) => {
        Swal.fire({ title: 'Delete Slider?', text: 'Cannot be undone.', icon: 'warning', showCancelButton: true, confirmButtonColor: '#e11d48' }).then(async res => {
            if (res.isConfirmed) {
                try {
                    // --- CHANGE 4: Use axiosInstance for deleting ---
                    await axiosInstance.delete(`/api/sliders/${id}`);
                    await fetchSliders();
                    Swal.fire('Deleted', '', 'success');
                } catch (error) {
                    if (error.response?.status !== 401) {
                        Swal.fire('Error', error.response?.data?.message || 'Delete failed', 'error');
                    }
                }
            }
        });
    };

    const toggleActive = async (id) => {
        const slide = slides.find(s => s._id === id);
        const newStatus = !slide.isActive;
        
        try {
            // --- CHANGE 5: Use axiosInstance for toggling active status ---
            await axiosInstance.put(`/api/sliders/${id}`, { isActive: newStatus });
            await fetchSliders();
        } catch (error) {
            if (error.response?.status !== 401) {
                Swal.fire('Error', 'Failed to toggle status', 'error');
            }
        }
    };

    const openModal = (mode, slide = null) => {
        setModalMode(mode);
        setSelectedSlide(slide);
        setIsModalOpen(true);
    };

    // --- YOUR JSX REMAINS UNCHANGED ---
    return (
        <div className="add-slider-wrapper">
            <div className="add-slider-container">
                <SliderFormModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} slide={selectedSlide} mode={modalMode} onSave={handleSave} />
                <div className="add-slider-header">
                    <div className="add-slider-header-title"><h1>Hero Sliders</h1></div>
                    <button className="add-slider-add-btn" onClick={() => openModal('add')}><FaPlus /> Add New Slider</button>
                </div>
                <div className="add-slider-controls-row">
                    <div className="add-slider-search-wrapper"><FaSearch className="add-slider-search-icon" /><input type="text" placeholder="Search by heading or text..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }} className="add-slider-search-input" /></div>
                    <div className="add-slider-filter-group"><label>Show:</label><select value={itemsPerPage} onChange={(e) => { setItemsPerPage(Number(e.target.value)); setCurrentPage(1); }} className="add-slider-select"><option value="10">10</option><option value="20">20</option><option value="30">30</option><option value="50">50</option></select></div>
                </div>
                <div className="add-slider-table-card">
                    <div className="add-slider-table-wrapper">
                        <table className="add-slider-table">
                            <thead>
                                <tr>
                                    <th className="add-slider-col-sno">S.No</th>
                                    <th className="add-slider-col-media">Media</th>
                                    <th className="add-slider-col-heading">Heading</th>
                                    <th className="add-slider-col-subtext">Sub Text</th>
                                    <th className="add-slider-col-type">Type</th>
                                    <th className="add-slider-col-date">Created At</th>
                                    <th className="add-slider-col-date">Updated At</th>
                                    <th className="add-slider-col-active">Active</th>
                                    <th className="add-slider-col-actions">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedSlides.map((slide, index) => (
                                    <React.Fragment key={slide._id}>
                                        <tr className="add-slider-row">
                                            <td className="add-slider-col-sno">{(currentPage - 1) * itemsPerPage + index + 1}</td>
                                            <td className="add-slider-col-media"><div className="add-slider-media-wrapper"><div className="add-slider-thumbnail" onClick={() => openModal('view', slide)}>{slide.type === 'image' ? <img src={`${API_BASE_URL}${slide.src}`} alt="thumb" onError={(e) => e.target.src='https://via.placeholder.com/80?text=Err'}/> : <video src={`${API_BASE_URL}${slide.src}`} muted />}</div><button className="add-slider-mobile-toggle-btn" onClick={() => toggleRow(slide._id)}>{expandedRow === slide._id ? <FaChevronUp /> : <FaChevronDown />}</button></div></td>
                                            <td className="add-slider-col-heading">{slide.heading || <span className="add-slider-empty">-</span>}</td>
                                            <td className="add-slider-col-subtext">{slide.subText || <span className="add-slider-empty">-</span>}</td>
                                            <td className="add-slider-col-type"><span className={`add-slider-type-badge ${slide.type}`}>{slide.type}</span></td>
                                            <td className="add-slider-col-date">{formatDate(slide.createdAt)}</td>
                                            <td className="add-slider-col-date">{slide.updatedAt ? formatDate(slide.updatedAt) : '-'}</td>
                                            <td className="add-slider-col-active"><div className="add-slider-switch" onClick={() => toggleActive(slide._id)}><div className={`add-slider-switch-handle ${slide.isActive ? 'active' : ''}`}></div></div></td>
                                            <td className="add-slider-col-actions"><div className="add-slider-action-icons"><button className="add-slider-icon-btn edit" onClick={() => openModal('edit', slide)}><FaEdit /></button><button className="add-slider-icon-btn delete" onClick={() => handleDelete(slide._id)}><FaTrashAlt /></button></div></td>
                                        </tr>
                                        {expandedRow === slide._id && (
                                            <tr className="add-slider-details-row"><td colSpan="10"><div className="add-slider-details-content"><div className="add-slider-details-grid">
                                                <div className="add-slider-detail-card add-slider-dc-heading"><div className="add-slider-detail-label">Heading</div><div className="add-slider-detail-value">{slide.heading || '-'}</div></div>
                                                <div className="add-slider-detail-card add-slider-dc-subtext"><div className="add-slider-detail-label">Sub Text</div><div className="add-slider-detail-value">{slide.subText || '-'}</div></div>
                                                <div className="add-slider-detail-card add-slider-dc-type"><div className="add-slider-detail-label">Type</div><div><span className={`add-slider-type-badge ${slide.type}`}>{slide.type}</span></div></div>
                                                <div className="add-slider-detail-card add-slider-dc-date-created"><div className="add-slider-detail-label">Created At</div><div className="add-slider-detail-value">{formatDate(slide.createdAt)}</div></div>
                                                <div className="add-slider-detail-card add-slider-dc-date-updated"><div className="add-slider-detail-label">Updated At</div><div className="add-slider-detail-value">{slide.updatedAt ? formatDate(slide.updatedAt) : '-'}</div></div>
                                                <div className="add-slider-detail-card add-slider-dc-active"><div className="add-slider-detail-label">Active</div><div className="add-slider-switch" onClick={() => toggleActive(slide._id)}><div className={`add-slider-switch-handle ${slide.isActive ? 'active' : ''}`}></div></div></div>
                                                <div className="add-slider-detail-card add-slider-dc-actions"><div className="add-slider-detail-label">Actions</div><div className="add-slider-action-icons" style={{ justifyContent: 'flex-start' }}><button className="add-slider-icon-btn edit" onClick={() => openModal('edit', slide)}><FaEdit /></button><button className="add-slider-icon-btn delete" onClick={() => handleDelete(slide._id)}><FaTrashAlt /></button></div></div>
                                            </div></div></td></tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
                <div className="add-slider-pagination-container">
                    <div className="add-slider-pagination-info">Showing {startResult} to {endResult} of {totalResults} results</div>
                    <div className="add-slider-pagination-controls">
                        <button className="add-slider-page-btn" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)}>Previous</button>
                        <span className="add-slider-page-count">Page {currentPage} of {totalPages || 1}</span>
                        <button className="add-slider-page-btn" disabled={currentPage === totalPages || totalPages === 0} onClick={() => setCurrentPage(p => p + 1)}>Next</button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddSlider;